import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '../components/Layout';
import ToastContainer from '../components/ToastContainer';
import StockBadge from '../components/StockBadge';
import SyncBadge from '../components/SyncBadge';
import { apiFetch } from '../utils/apiClient';
import { fileToCoverDataUrl } from '../utils/image';
import { formatPrice, formatDate } from '../utils/format';
import { useVendor } from '../contexts/VendorContext';

interface InventoryItemDetails {
  id: number;
  book: {
    id: number;
    title: string;
    isbn_13: string;
    isbn_10: string;
    authors: string;
    publisher: string;
    format: string;
    language?: string;
    categories?: string | null;
    cover_image_url: string | null;
  };
  vendor: { id: number; name: string };
  cover_image_url?: string | null;
  has_pending_cover_image?: boolean;
  condition: string;
  selling_price: string;
  quantity: number;
  quantity_sold: number;
  quantity_reserved: number;
  available_quantity: number;
  location: string | null;
  sku: string | null;
  notes: string | null;
  shopify_product_id: string;
  shopify_variant_id: string;
  shopify_sync_status: string;
  shopify_synced_at: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface SyncLog {
  id: number;
  sync_type: string;
  status: string;
  error_message: string;
  started_at: string;
  duration_seconds: number;
}

interface ToastData {
  id: string;
  message: string;
  type: 'success' | 'error' | 'info' | 'warning';
}

const CONDITIONS = ['new', 'like_new', 'very_good', 'good', 'acceptable'];

interface EditState {
  selling_price: string;
  quantity: string;
  condition: string;
  location: string;
  notes: string;
  is_active: boolean;
}

const ItemDetails: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { settings } = useVendor();
  const [item, setItem] = useState<InventoryItemDetails | null>(null);
  const [syncLogs, setSyncLogs] = useState<SyncLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [syncing, setSyncing] = useState(false);
  const [toasts, setToasts] = useState<ToastData[]>([]);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [edit, setEdit] = useState<EditState | null>(null);
  const [editError, setEditError] = useState('');
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  const addToast = (message: string, type: ToastData['type']) => {
    setToasts((prev) => [...prev, { id: `${Date.now()}-${Math.random()}`, message, type }]);
  };
  const removeToast = (toastId: string) => setToasts((prev) => prev.filter((x) => x.id !== toastId));

  const fetchItemDetails = useCallback(async () => {
    if (!id) return;
    try {
      const response = await apiFetch(`/api/inventory/${id}/`);
      if (!response.ok) throw new Error(t('common.genericError'));
      const data = await response.json();
      setItem(data);
      setError('');
    } catch (err: any) {
      setError(err.message || t('common.genericError'));
    } finally {
      setLoading(false);
    }
  }, [id, t]);

  const fetchSyncLogs = useCallback(async () => {
    if (!id) return;
    try {
      const response = await apiFetch(`/api/inventory/${id}/sync-logs/`);
      if (response.ok) {
        const data = await response.json();
        setSyncLogs(data.results || data || []);
      }
    } catch (err) {
      console.error('Failed to fetch sync logs:', err);
    }
  }, [id]);

  useEffect(() => {
    fetchItemDetails();
    fetchSyncLogs();
  }, [fetchItemDetails, fetchSyncLogs]);

  // Poll while a background sync is pending so the status flips on its own
  useEffect(() => {
    if (item?.shopify_sync_status !== 'pending') return;
    const timer = window.setInterval(() => {
      fetchItemDetails();
      fetchSyncLogs();
    }, 5000);
    return () => window.clearInterval(timer);
  }, [item?.shopify_sync_status, fetchItemDetails, fetchSyncLogs]);

  const startEdit = () => {
    if (!item) return;
    setEdit({
      selling_price: item.selling_price,
      quantity: String(item.quantity),
      condition: item.condition,
      location: item.location || '',
      notes: item.notes || '',
      is_active: item.is_active,
    });
    setEditError('');
    setEditing(true);
  };

  const saveEdit = async () => {
    if (!edit || !id) return;
    setEditError('');
    setSaving(true);
    try {
      const response = await apiFetch(`/api/inventory/${id}/`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          selling_price: edit.selling_price,
          quantity: parseInt(edit.quantity, 10) || 0,
          condition: edit.condition,
          location: edit.location || null,
          notes: edit.notes || null,
          is_active: edit.is_active,
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        const first = data.error || data.selling_price?.[0] || data.quantity?.[0] || data.detail || t('common.genericError');
        throw new Error(String(first));
      }
      setItem(data);
      setEditing(false);
      const sync = data.sync;
      if (sync?.status === 'synced') addToast(t('item.savedSynced'), 'success');
      else if (sync?.status === 'queued') addToast(t('item.savedSyncing'), 'success');
      else if (sync?.status === 'failed') addToast(`${t('item.savedSyncFailed')} ${sync.message || ''}`, 'warning');
      else addToast(t('item.saved'), 'success');
      fetchSyncLogs();
    } catch (err: any) {
      setEditError(err.message || t('common.genericError'));
    } finally {
      setSaving(false);
    }
  };

  const handlePhotoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !id) return;
    setUploadingPhoto(true);
    try {
      const cover_image_data = await fileToCoverDataUrl(file);
      const response = await apiFetch(`/api/inventory/${id}/`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ cover_image_data }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(String(data.error || data.detail || t('common.genericError')));
      setItem(data);
      const sync = data.sync;
      if (sync?.status === 'synced') addToast(t('item.photoSynced'), 'success');
      else if (sync?.status === 'failed') addToast(`${t('item.photoSyncFailed')} ${sync.message || ''}`, 'warning');
      else addToast(t('item.photoSaved'), 'success');
      fetchSyncLogs();
    } catch (err: any) {
      addToast(err.message || t('common.genericError'), 'error');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleDelete = async () => {
    if (!item || !id) return;
    if (!window.confirm(t('item.deleteConfirm', { title: item.book.title }))) return;
    setDeleting(true);
    try {
      const response = await apiFetch(`/api/inventory/${id}/delete-item/`, { method: 'DELETE' });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || data.message || t('common.genericError'));
      navigate('/inventory', { replace: true });
    } catch (err: any) {
      addToast(err.message || t('common.genericError'), 'error');
      setDeleting(false);
    }
  };

  const handleSyncShopify = async () => {
    if (!id) return;
    setSyncing(true);
    try {
      const response = await apiFetch(`/api/inventory/${id}/sync-shopify/`, { method: 'POST' });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || data.error || t('common.genericError'));
      await fetchItemDetails();
      await fetchSyncLogs();
      addToast(data.message || t('item.saved'), 'success');
    } catch (err: any) {
      addToast(err.message || t('common.genericError'), 'error');
      await fetchItemDetails();
      await fetchSyncLogs();
    } finally {
      setSyncing(false);
    }
  };

  const formatDuration = (seconds: number | null) => {
    if (!seconds) return '—';
    if (seconds < 1) return `${(seconds * 1000).toFixed(0)}ms`;
    return `${seconds.toFixed(2)}s`;
  };

  if (loading) {
    return (
      <Layout>
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600">{t('common.loading')}</p>
        </div>
      </Layout>
    );
  }

  if (error || !item) {
    return (
      <Layout>
        <div className="rounded-md bg-red-50 p-4">
          <p className="text-sm text-red-800">{error || t('common.genericError')}</p>
          <button onClick={() => navigate('/inventory')} className="mt-4 text-blue-600">← {t('item.back')}</button>
        </div>
      </Layout>
    );
  }

  const inputClass =
    'block w-full rounded-md border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-base sm:text-sm px-3 py-2.5';
  const Row: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
    <div>
      <dt className="text-sm font-medium text-gray-500">{label}</dt>
      <dd className="mt-1 text-sm text-gray-900">{children}</dd>
    </div>
  );

  return (
    <Layout>
      <ToastContainer toasts={toasts} onRemove={removeToast} />

      <div className="w-full max-w-4xl mx-auto">
        <button onClick={() => navigate('/inventory')} className="mb-4 text-blue-600 active:text-blue-700 flex items-center py-2 min-h-[44px] touch-manipulation">
          ← {t('item.back')}
        </button>

        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="px-4 sm:px-6 py-4 border-b border-gray-200 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
            <div className="min-w-0">
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900">{item.book.title}</h1>
              <p className="text-xs sm:text-sm text-gray-500 mt-1">ISBN-13: {item.book.isbn_13}</p>
              <div className="mt-2 flex flex-wrap gap-2">
                <StockBadge available={item.available_quantity} total={item.quantity} />
                <SyncBadge status={item.shopify_sync_status} />
                {!item.is_active && (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">{t('item.unlisted')}</span>
                )}
              </div>
            </div>
            {!editing && (
              <div className="flex gap-2 flex-shrink-0">
                <button onClick={startEdit} className="px-4 py-2 rounded-md bg-blue-600 text-white text-sm font-medium min-h-[44px] touch-manipulation">
                  {t('item.editItem')}
                </button>
                <button
                  onClick={handleDelete}
                  disabled={deleting}
                  className="px-4 py-2 rounded-md border border-red-300 text-red-700 text-sm font-medium min-h-[44px] touch-manipulation disabled:opacity-50"
                >
                  {t('item.deleteItem')}
                </button>
              </div>
            )}
          </div>

          <div className="px-4 sm:px-6 py-4 space-y-6">
            {/* Edit form */}
            {editing && edit && (
              <div className="rounded-lg border border-blue-200 bg-blue-50/40 p-4 space-y-4">
                <h2 className="text-base font-semibold text-gray-900">{t('item.editItem')}</h2>
                {editError && (
                  <div className="rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-800">{editError}</div>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('item.sellingPrice')} ({t('common.currency')})</label>
                    <input type="number" step="0.01" min="0" className={inputClass} value={edit.selling_price} onChange={(e) => setEdit({ ...edit, selling_price: e.target.value })} />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('item.totalQty')}</label>
                    <input type="number" min="0" className={inputClass} value={edit.quantity} onChange={(e) => setEdit({ ...edit, quantity: e.target.value })} />
                    <p className="mt-1 text-xs text-gray-500">{t('item.sold')}: {item.quantity_sold} · {t('item.reserved')}: {item.quantity_reserved}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('inventory.condition')}</label>
                    <select className={`${inputClass} min-h-[44px]`} value={edit.condition} onChange={(e) => setEdit({ ...edit, condition: e.target.value })}>
                      {CONDITIONS.map((c) => (
                        <option key={c} value={c}>{t(`condition.${c}`)}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('item.location')}</label>
                    <input type="text" className={inputClass} value={edit.location} onChange={(e) => setEdit({ ...edit, location: e.target.value })} />
                  </div>
                  <div className="flex items-center gap-3 pt-6">
                    <input id="is_active" type="checkbox" className="h-5 w-5 rounded border-gray-300 text-blue-600" checked={edit.is_active} onChange={(e) => setEdit({ ...edit, is_active: e.target.checked })} />
                    <label htmlFor="is_active" className="text-sm text-gray-700">{t('item.listed')}</label>
                  </div>
                  <div className="sm:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('item.notes')}</label>
                    <textarea rows={2} className={inputClass} value={edit.notes} onChange={(e) => setEdit({ ...edit, notes: e.target.value })} />
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row gap-2 sm:justify-end">
                  <button onClick={() => setEditing(false)} disabled={saving} className="px-4 py-2 rounded-md border border-gray-300 text-gray-700 text-sm font-medium min-h-[44px]">
                    {t('common.cancel')}
                  </button>
                  <button onClick={saveEdit} disabled={saving} className="px-4 py-2 rounded-md bg-blue-600 text-white text-sm font-medium min-h-[44px] disabled:opacity-50">
                    {saving ? t('common.saving') : t('common.save')}
                  </button>
                </div>
              </div>
            )}

            {/* Book Information */}
            <div>
              <h2 className="text-base sm:text-lg font-semibold text-gray-900 mb-3">{t('item.bookInfo')}</h2>
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-24 sm:w-32">
                  <div className="w-24 h-32 sm:w-32 sm:h-44 bg-gray-100 rounded-lg overflow-hidden flex items-center justify-center text-xs text-gray-400">
                    {(item.cover_image_url || item.book.cover_image_url) ? (
                      <img src={item.cover_image_url || item.book.cover_image_url || ''} alt={item.book.title} className="w-full h-full object-cover" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                    ) : t('item.noPhoto')}
                  </div>
                  <label className={`mt-2 block text-center text-xs font-medium cursor-pointer ${uploadingPhoto ? 'text-gray-400' : 'text-brand-600 hover:text-brand-700'}`}>
                    {uploadingPhoto ? t('item.photoUploading') : t('item.changePhoto')}
                    <input type="file" accept="image/*" className="hidden" onChange={handlePhotoChange} disabled={uploadingPhoto} />
                  </label>
                  {item.has_pending_cover_image && (
                    <p className="mt-1 text-center text-[11px] text-amber-700">{t('item.photoPending')}</p>
                  )}
                </div>
                <dl className="grid grid-cols-1 gap-x-4 gap-y-3 sm:grid-cols-2 flex-1">
                  <Row label={t('item.authors')}>{item.book.authors || '—'}</Row>
                  <Row label={t('item.publisher')}>{item.book.publisher || '—'}</Row>
                  <Row label={t('item.format')}><span className="capitalize">{item.book.format}</span></Row>
                  <Row label={t('item.language')}>{item.book.language ? t(`language.${item.book.language}`, item.book.language) : '—'}</Row>
                  <Row label={t('item.isbn10')}>{item.book.isbn_10 || '—'}</Row>
                  <Row label={t('item.categories')}>{item.book.categories || '—'}</Row>
                </dl>
              </div>
            </div>

            {/* Inventory */}
            <div>
              <h2 className="text-base sm:text-lg font-semibold text-gray-900 mb-3">{t('item.inventoryInfo')}</h2>
              <dl className="grid grid-cols-2 gap-x-4 gap-y-3 sm:grid-cols-3">
                <Row label={t('inventory.vendor')}>{item.vendor.name}</Row>
                <Row label={t('inventory.condition')}>{t(`condition.${item.condition}`, item.condition)}</Row>
                <Row label={t('item.totalQty')}>{item.quantity}</Row>
                <Row label={t('item.availableQty')}>{item.available_quantity}</Row>
                <Row label={t('item.sold')}>{item.quantity_sold}</Row>
                <Row label={t('item.reserved')}>{item.quantity_reserved}</Row>
                <Row label={t('item.location')}>{item.location || '—'}</Row>
                <Row label={t('item.sku')}>{item.sku || '—'}</Row>
                <Row label={t('item.notes')}>{item.notes || '—'}</Row>
              </dl>
            </div>

            {/* Pricing */}
            <div>
              <h2 className="text-base sm:text-lg font-semibold text-gray-900 mb-3">{t('item.pricing')}</h2>
              <dl className="grid grid-cols-2 gap-x-4 gap-y-3 sm:grid-cols-3">
                <Row label={t('item.sellingPrice')}><span className="font-semibold">{formatPrice(item.selling_price)}</span></Row>
              </dl>
            </div>

            {/* Shopify */}
            <div>
              <h2 className="text-base sm:text-lg font-semibold text-gray-900 mb-3">{t('item.shopify')}</h2>
              <dl className="grid grid-cols-2 gap-x-4 gap-y-3 sm:grid-cols-4">
                <Row label={t('item.syncStatus')}><SyncBadge status={item.shopify_sync_status} /></Row>
                <Row label={t('item.productId')}>{item.shopify_product_id || '—'}</Row>
                <Row label={t('item.variantId')}>{item.shopify_variant_id || '—'}</Row>
                <Row label={t('item.lastSynced')}>{formatDate(item.shopify_synced_at)}</Row>
              </dl>
              <div className="mt-4 flex flex-col sm:flex-row sm:items-center gap-3">
                <button
                  onClick={handleSyncShopify}
                  disabled={syncing}
                  className="w-full sm:w-auto bg-white border border-blue-600 text-blue-700 px-4 py-3 sm:py-2 rounded-md active:bg-blue-50 transition-colors disabled:opacity-50 flex items-center justify-center min-h-[44px] touch-manipulation"
                >
                  {syncing ? t('item.syncing') : t('item.syncNow')}
                </button>
                {settings.auto_sync && <p className="text-xs text-gray-500">{settings.sync_mode === 'background' ? t('item.autoSyncNote') : t('item.inlineSyncNote')}</p>}
              </div>
            </div>

            {/* Sync history */}
            {syncLogs.length > 0 && (
              <div>
                <h2 className="text-base sm:text-lg font-semibold text-gray-900 mb-3">{t('item.syncHistory')}</h2>
                <div className="overflow-x-auto -mx-4 sm:mx-0">
                  <table className="min-w-full divide-y divide-gray-300 text-xs sm:text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left font-medium text-gray-500 uppercase">{t('item.type')}</th>
                        <th className="px-3 py-2 text-left font-medium text-gray-500 uppercase">{t('common.status')}</th>
                        <th className="px-3 py-2 text-left font-medium text-gray-500 uppercase hidden sm:table-cell">{t('item.started')}</th>
                        <th className="px-3 py-2 text-left font-medium text-gray-500 uppercase hidden md:table-cell">{t('item.duration')}</th>
                        <th className="px-3 py-2 text-left font-medium text-gray-500 uppercase">{t('item.error')}</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {syncLogs.map((log) => (
                        <tr key={log.id}>
                          <td className="px-3 py-2 whitespace-nowrap text-gray-900">{log.sync_type.replace('_', ' ')}</td>
                          <td className="px-3 py-2 whitespace-nowrap">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                              log.status === 'success' ? 'bg-green-100 text-green-800' : log.status === 'failed' ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'
                            }`}>{log.status}</span>
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap text-gray-500 hidden sm:table-cell">{formatDate(log.started_at)}</td>
                          <td className="px-3 py-2 whitespace-nowrap text-gray-500 hidden md:table-cell">{formatDuration(log.duration_seconds)}</td>
                          <td className="px-3 py-2 text-gray-500">
                            {log.error_message ? <span className="text-red-600 block max-w-xs truncate" title={log.error_message}>{log.error_message}</span> : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default ItemDetails;
